import { useEffect, useState } from "react";

import { api, ApiError } from "@/services/api";
import type { BookingBlDocument } from "@/types/api";

/** Filename tokens that unambiguously identify house vs master bills. */
export function hintBlDocumentType(
  filename: string,
): "hbl" | "mbl" | null {
  const name = filename.replace(/^.*[/\\]/, "");
  const stem = name.includes(".") ? name.replace(/\.[^.]+$/, "") : name;
  const hay = `${stem} ${name}`;
  if (
    /(?:^|[\s_\-\d])(?:house[\s_\-]*bill(?:[\s_\-]*of[\s_\-]*lading)?|h\.?\s*b\/?l|hbl)(?:$|[\s_\-\.])/i.test(
      hay,
    )
  ) {
    return "hbl";
  }
  if (
    /(?:^|[\s_\-\d])(?:master[\s_\-]*bill(?:[\s_\-]*of[\s_\-]*lading)?|m\.?\s*b\/?l|mbl|sea[\s_\-]*way[\s_\-]*bill|swb)(?:$|[\s_\-\.])/i.test(
      hay,
    )
  ) {
    return "mbl";
  }
  return null;
}

/** Prefer an unambiguous filename hint over a stale classified type. */
export function resolvedBlDocumentType(doc: {
  document_type: string;
  filename: string;
}): string {
  return hintBlDocumentType(doc.filename) ?? doc.document_type;
}

/** Human label for a bill-of-lading document type. */
export function blDocumentTypeLabel(documentType: string): string {
  switch (documentType.toLowerCase()) {
    case "hbl":
      return "House B/L";
    case "mbl":
      return "Master B/L";
    case "bl":
      return "Bill of lading";
    default:
      return "Bill of lading";
  }
}

/** Short label for a compact badge (HBL / MBL / BL). */
export function blDocumentTypeBadge(documentType: string): string {
  const t = documentType.toLowerCase();
  if (t === "hbl" || t === "mbl" || t === "bl") return t.toUpperCase();
  return "B/L";
}

/**
 * A document is displayable when its backing doc-intel job succeeded (ok /
 * partial) and it carries at least a B/L number (the minimum useful identity).
 */
export function isBlDocumentDisplayable(doc: BookingBlDocument): boolean {
  const status = doc.parse_status?.toLowerCase();
  const parsedOk = status == null || status === "ok" || status === "partial";
  const hasIdentity =
    (doc.bl_number != null && doc.bl_number.trim() !== "") ||
    doc.carrier != null ||
    doc.vessel != null;
  return parsedOk && hasIdentity;
}

/** Primary line for a document row: B/L number, falling back to the filename. */
export function blDocumentTitle(doc: BookingBlDocument): string {
  const num = doc.bl_number?.trim();
  if (num) return num;
  return doc.filename;
}

/** Canonical carrier B/L from the booking MV (`carrier_bl_number`). */
export function normalizeCarrierBlNumber(
  value?: string | null,
): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/** Short provenance label when the MV B/L did not come from a linked document row. */
export function carrierBlProvenanceLabel(
  source?: string | null,
): string | undefined {
  if (source === "carrier_tracking") return "Carrier tracking";
  return undefined;
}

export interface BlDocumentTypeGroup {
  type: string;
  label: string;
  documents: BookingBlDocument[];
}

/**
 * Collapse bills of the same kind so the commercial card shows one key
 * ("Master B/L") with a stacked list of files underneath.
 */
export function groupBlDocumentsByType(
  documents: BookingBlDocument[],
): BlDocumentTypeGroup[] {
  const groups: BlDocumentTypeGroup[] = [];
  const indexByType = new Map<string, number>();

  for (const doc of documents) {
    const type = resolvedBlDocumentType(doc).toLowerCase();
    const existing = indexByType.get(type);
    if (existing != null) {
      groups[existing].documents.push(doc);
      continue;
    }
    indexByType.set(type, groups.length);
    groups.push({
      type,
      label: blDocumentTypeLabel(type),
      documents: [doc],
    });
  }

  return groups;
}

export function useBookingBlDocuments(sourceId: string | null) {
  const [documents, setDocuments] = useState<BookingBlDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sourceId) {
      setDocuments([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .getBookingBlDocuments(sourceId)
      .then((next) => {
        if (cancelled) return;
        setDocuments(next);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load BL documents", err);
        setDocuments([]);
        setError(
          err instanceof ApiError
            ? err.message
            : "Could not load bill-of-lading documents. Please try again.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sourceId]);

  return { documents, loading, error };
}
